import React, { useEffect, useMemo, useState } from "react";
import {
  BadgeDollarSign,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Loader2,
  Ticket,
  TrendingUp,
} from "lucide-react";

export default function EarningsDashboard({ userEmail }) {
  const [events, setEvents] = useState([]);
  const [ticketsByEvent, setTicketsByEvent] = useState({});
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!userEmail) {
        setLoading(false);
        return;
      }

      try {
        const eventsRes = await fetch(
          `/api/myEvents?email=${encodeURIComponent(userEmail)}`
        );
        const eventsData = await eventsRes.json();

        if (!eventsRes.ok || !eventsData.success) {
          throw new Error("Failed to load events.");
        }

        const loadedEvents = Array.isArray(eventsData.events)
          ? eventsData.events
          : [];

        setEvents(loadedEvents);

        const ticketResults = await Promise.all(
          loadedEvents.map(async (event) => {
            try {
              const res = await fetch(`/api/ticketsForEvent/${event._id}`);
              const data = await res.json();

              return {
                eventId: event._id,
                tickets: res.ok && data.success && Array.isArray(data.tickets)
                  ? data.tickets
                  : [],
              };
            } catch (err) {
              console.error(`Failed to load tickets for event ${event._id}:`, err);
              return {
                eventId: event._id,
                tickets: [],
              };
            }
          })
        );

        const nextTicketsByEvent = ticketResults.reduce((acc, result) => {
          acc[result.eventId] = result.tickets;
          return acc;
        }, {});

        setTicketsByEvent(nextTicketsByEvent);
      } catch (err) {
        console.error(err);
        alert("Error loading earnings dashboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userEmail]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(Number(amount) || 0);

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return dateString;

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const getEventBreakdown = (event) => {
    const tickets = ticketsByEvent[event._id] || [];
    const breakdownMap = new Map();

    tickets.forEach((ticket) => {
      const title = ticket.ticketTierTitle || "General Admission";
      const price = Number(ticket.ticketTierPrice) || 0;

      if (!breakdownMap.has(title)) {
        breakdownMap.set(title, {
          ticketTierTitle: title,
          ticketTierPrice: price,
          sold: 0,
          grossSales: 0,
        });
      }

      const row = breakdownMap.get(title);
      row.sold += 1;
      row.grossSales += price;
    });

    return Array.from(breakdownMap.values()).sort((a, b) =>
      a.ticketTierTitle.localeCompare(b.ticketTierTitle)
    );
  };

  const eventReports = useMemo(() => {
    return events.map((event) => {
      const tickets = ticketsByEvent[event._id] || [];
      const breakdown = getEventBreakdown(event);

      const ticketsSold = tickets.length;
      const totalSales = tickets.reduce((sum, ticket) => {
        return sum + (Number(ticket.ticketTierPrice) || 0);
      }, 0);

      const averageTicketPrice =
        ticketsSold > 0 ? totalSales / ticketsSold : 0;

      return {
        event,
        ticketsSold,
        totalSales,
        averageTicketPrice,
        breakdown,
      };
    });
  }, [events, ticketsByEvent]);

  const totalSales = eventReports.reduce(
    (sum, report) => sum + report.totalSales,
    0
  );

  const totalTicketsSold = eventReports.reduce(
    (sum, report) => sum + report.ticketsSold,
    0
  );

  const activeEvents = eventReports.length;

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-10 shadow-sm">
            <div className="flex items-center gap-3 text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium">
                Loading earnings dashboard...
              </span>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Earnings Dashboard
          </h1>
          <p className="mt-2 text-base text-slate-600">
            View ticket sales totals and per-ticket breakdowns for your events.
          </p>
        </header>

        <section className="mb-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Total Sales
                </p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                  {formatCurrency(totalSales)}
                </p>
              </div>
              <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-700">
                <BadgeDollarSign className="h-6 w-6" />
              </div>
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Tickets Sold
                </p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                  {totalTicketsSold}
                </p>
              </div>
              <div className="rounded-2xl bg-blue-50 p-3 text-blue-700">
                <Ticket className="h-6 w-6" />
              </div>
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Events Tracked
                </p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                  {activeEvents}
                </p>
              </div>
              <div className="rounded-2xl bg-amber-50 p-3 text-amber-700">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </div>
        </section>

        {eventReports.length === 0 ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white px-8 py-16 text-center shadow-sm">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              No sales data yet
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
              Once you create events and sell tickets, your sales totals will
              appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {eventReports.map((report) => {
              const { event, ticketsSold, totalSales, averageTicketPrice, breakdown } =
                report;

              const isExpanded = expandedEventId === event._id;

              return (
                <section
                  key={event._id}
                  className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm"
                >
                  <div className="p-6 sm:p-8">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                          {event.name}
                        </h2>

                        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                          <CalendarDays className="h-4 w-4" />
                          <span>{formatDate(event.dateTime)}</span>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-slate-900 px-5 py-4 text-white lg:min-w-56 lg:text-right">
                        <p className="text-sm font-medium text-white/70">
                          Total Sales
                        </p>
                        <p className="mt-1 text-3xl font-semibold tracking-tight">
                          {formatCurrency(totalSales)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 grid gap-4 sm:grid-cols-3">
                      <div className="rounded-2xl bg-slate-50 p-4">
                        <p className="text-sm font-medium text-slate-500">
                          Tickets Sold
                        </p>
                        <p className="mt-1 text-xl font-semibold text-slate-900">
                          {ticketsSold}
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-4">
                        <p className="text-sm font-medium text-slate-500">
                          Average Ticket
                        </p>
                        <p className="mt-1 text-xl font-semibold text-slate-900">
                          {formatCurrency(averageTicketPrice)}
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-4">
                        <p className="text-sm font-medium text-slate-500">
                          Ticket Types Sold
                        </p>
                        <p className="mt-1 text-xl font-semibold text-slate-900">
                          {breakdown.length}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedEventId(isExpanded ? null : event._id)
                        }
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 ring-1 ring-slate-200 transition hover:bg-slate-50"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="h-4 w-4" />
                            Hide Breakdown
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4" />
                            View Breakdown
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-slate-200 bg-slate-50/70 px-6 py-6 sm:px-8">
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-slate-900">
                          Per-ticket breakdown
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Gross sales grouped by ticket tier.
                        </p>
                      </div>

                      {breakdown.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
                          No tickets have been sold for this event yet.
                        </div>
                      ) : (
                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                          <table className="w-full text-left text-sm">
                            <thead className="bg-white text-slate-500">
                              <tr>
                                <th className="px-4 py-3 font-semibold">
                                  Ticket Tier
                                </th>
                                <th className="px-4 py-3 font-semibold">
                                  Sold
                                </th>
                                <th className="px-4 py-3 font-semibold">
                                  Price
                                </th>
                                <th className="px-4 py-3 font-semibold">
                                  Gross Sales
                                </th>
                              </tr>
                            </thead>

                            <tbody className="divide-y divide-slate-200">
                              {breakdown.map((row) => (
                                <tr key={row.ticketTierTitle}>
                                  <td className="px-4 py-3 font-medium text-slate-900">
                                    {row.ticketTierTitle}
                                  </td>
                                  <td className="px-4 py-3 text-slate-700">
                                    {row.sold}
                                  </td>
                                  <td className="px-4 py-3 text-slate-700">
                                    {formatCurrency(row.ticketTierPrice)}
                                  </td>
                                  <td className="px-4 py-3 font-semibold text-slate-900">
                                    {formatCurrency(row.grossSales)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}